import React, { useEffect, useState } from 'react';
import {
  alpha,
  Avatar,
  Box,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Close, Edit, PersonAdd } from '@mui/icons-material';
import type { Contact } from '@otw/models/leads/contacts';
import type {
  ContactCompany,
  ContactCompanyRole,
} from '@otw/models/leads/contactCompanies';
import { Lead } from '@otw/models/leads/lead';
import { AdminDialog } from 'components/AdminDialog';
import { useIsMobile } from 'hooks/screen/useIsMobile';
import { TOUCH_MIN } from 'core/theme.tokens';
import { useTheme } from '@mui/material/styles';
import { LeadForm } from '../forms';

/** Minimal client shape when opening Create Lead with a pre-selected client (e.g. from Create User). */
export interface InitialClientForLead {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  company_name?: string;
  displayName?: string;
}

/** Member contact + company role passed into the lead form for business-contact prefill. */
export interface CompanyMemberForLead {
  contact: Contact;
  role: ContactCompanyRole;
}

/** Prefill lead basics from a CRM contact without pretending a portal client is linked. */
export interface ContactPrefillForLead {
  customerName: string;
  email?: string;
  phone?: string;
  company?: string;
}

interface LeadFormDialogProps {
  open: boolean;
  onClose: () => void;
  selectedLead: Lead | null;
  isCreating: boolean;
  initialClient?: InitialClientForLead | null;
  /** When set without a matching portal user, form fields prefill but client search stays required. */
  contactPrefill?: ContactPrefillForLead | null;
  /** Block create until a real client is selected (used from CRM contact preview). */
  requireLinkedClient?: boolean;
  /**
   * The {@link ContactCompany} the lead is being created for. When provided
   * the LeadForm prefills `company`, `companyId`, and `businessAddress`
   * from this record and switches the Business Contact section to a picker.
   */
  selectedCompany?: ContactCompany | null;
  /** Hydrated member contacts of the {@link selectedCompany}; used as Business Contact options. */
  companyMembers?: CompanyMemberForLead[];
  /** When set, pre-selects this contact in the business-contact picker and prefills their company role. */
  defaultBusinessContactId?: string | null;
  /**
   * CRM contact id to store on the sales lead (`Lead.contactId`).
   * Used when creating from Contact / Company CRM surfaces.
   */
  initialContactId?: string | null;
  /** Fires after a successful create, before the dialog closes. */
  onLeadCreated?: (lead: Lead) => void | Promise<void>;
}

const LeadFormDialog: React.FC<LeadFormDialogProps> = ({
  open,
  onClose,
  selectedLead,
  isCreating,
  initialClient = null,
  contactPrefill = null,
  requireLinkedClient = false,
  selectedCompany = null,
  companyMembers,
  defaultBusinessContactId = null,
  initialContactId = null,
  onLeadCreated,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();

  // Track if the dialog has been opened with this lead so the form can re-init
  // when reopening with a new selection. (Existing logic preserved.)
  const [initializedWithLead, setInitializedWithLead] = useState<string | null>(null);

  useEffect(() => {
    if (open && selectedLead && selectedLead.id !== initializedWithLead) {
      setInitializedWithLead(selectedLead.id);
    }
  }, [open, selectedLead, initializedWithLead]);

  const handleClearSelection = () => {
    onClose();
    if (!open) setInitializedWithLead(null);
  };

  const accent = theme.palette.primary.main;
  const subtitle = isCreating
    ? 'Capture client info, truck preferences, and connect existing inventory or applications.'
    : selectedLead?.customerName
      ? `Update details for ${selectedLead.customerName}${
          selectedLead.company ? ` · ${selectedLead.company}` : ''
        }`
      : 'Update lead details, status, and linked records.';

  return (
    <AdminDialog
      open={open}
      maxWidth="md"
      fullScreen={isMobile}
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') onClose();
      }}
      PaperProps={{
        sx: {
          maxHeight: { xs: '100vh', sm: '92vh' },
          height: 'auto',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          p: { xs: 2, md: 2.5 },
          background: `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, ${alpha(accent, 0.04)} 100%)`,
          borderBottom: `1px solid ${alpha(accent, 0.18)}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: alpha(accent, 0.18),
              color: accent,
              width: 44,
              height: 44,
            }}
          >
            {isCreating ? <PersonAdd /> : <Edit />}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="overline" sx={{ color: accent, letterSpacing: 1.2, fontWeight: 800, fontSize: '1rem' }}>
              {isCreating ? 'New lead' : 'Editing lead'}
            </Typography>
            <Typography
              variant="caption"
              color="text.primary"
              sx={{ display: 'block', lineHeight: 1.4 }}
              noWrap
            >
              {subtitle}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              minWidth: TOUCH_MIN,
              minHeight: TOUCH_MIN,
              color: 'text.secondary',
              '&:hover': { bgcolor: alpha(accent, 0.08), color: accent },
            }}
          >
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers={false}
        sx={{
          p: { xs: 1.5, md: 3 },
          pt: { xs: 2, md: 2.5 },
          overflowX: 'hidden',
          ...(isMobile
            ? {
                '& .MuiInputBase-input, & .MuiSelect-select, & .MuiAutocomplete-input': {
                  fontSize: '1rem',
                },
                '& .MuiGrid-item': {
                  flexBasis: '100% !important',
                  maxWidth: '100% !important',
                },
                '& form > div:last-of-type': {
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 3,
                  bgcolor: 'background.paper',
                  borderTop: 1,
                  borderColor: 'divider',
                  mx: -1.5,
                  px: 1.5,
                  py: 1.5,
                  '& .MuiButton-root': { minHeight: TOUCH_MIN },
                },
              }
            : {}),
        }}
      >
        <LeadForm
          selectedLead={selectedLead}
          onClearSelection={handleClearSelection}
          isCreating={isCreating}
          initialClient={initialClient}
          contactPrefill={contactPrefill}
          requireLinkedClient={requireLinkedClient}
          selectedCompany={selectedCompany}
          companyMembers={companyMembers}
          defaultBusinessContactId={defaultBusinessContactId}
          initialContactId={initialContactId}
          onLeadCreated={onLeadCreated}
        />
      </DialogContent>
    </AdminDialog>
  );
};

export default LeadFormDialog;
